/**
 * SoleadLab 2.0 - Button Component
 *
 * Consistent button styling across the application.
 * Supports multiple variants, sizes, and states.
 */

import { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';
import './Button.css';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'warning' | 'accent';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size of the button */
  size?: ButtonSize;
  /** Show loading spinner */
  loading?: boolean;
  /** Icon to display before text */
  icon?: ReactNode;
  /** Icon to display after text */
  iconRight?: ReactNode;
  /** Make button full width */
  fullWidth?: boolean;
}

/**
 * Button component with consistent styling
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconRight,
      fullWidth = false,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const classes = [
      'sl-btn',
      `sl-btn--${variant}`,
      `sl-btn--${size}`,
      fullWidth && 'sl-btn--full',
      loading && 'sl-btn--loading',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <span className="sl-btn__spinner" />}
        {!loading && icon && <span className="sl-btn__icon">{icon}</span>}
        {children && <span className="sl-btn__text">{children}</span>}
        {!loading && iconRight && <span className="sl-btn__icon">{iconRight}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
